import React, { ReactElement, useContext, useEffect } from 'react'
import { ArtData } from '@/database/art'
import Button from '@/components/Button'
import { Context } from '@/components/Context'
import Claim from '@/components/boxes/Claim'
import Price from '@/components/boxes/Price'
import ModalButtons from '@/components/ModalButtons'
import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import { crazyAmount, priceText, transferFeeText } from '@/utils/formatting'
import Box from '@/components/boxes/Box'
import LoadImageButtonOnDemand from '@/components/LoadImageButtonOnDemand'
import ClaimsReceivedTooltip from '@/components/tooltips/ClaimsReceivedTooltip'
import VotesTooltip from '@/components/tooltips/VotesTooltip'
import ClaimsMadeTooltip from '@/components/tooltips/ClaimsMadeTooltip'
import DetailsTabs from '@/components/market/DetailsTabs'
import { UpdateType } from '@/types/websocket'

const tabName = '#market'

const ModelMyMarketCardDetails = ({ artData, onClose }: { artData: ArtData; onClose: () => void }): ReactElement => {
    const { accountSignature, acceptOffer, acceptBid, loading, currentAccount, setActiveUpdates } = useContext(Context)

    useEffect(() => {
        setActiveUpdates([UpdateType.MARKETS, UpdateType.VOTES, UpdateType.PORTFOLIO])
    }, [])

    return (
        <div>
            {artData && (
                <div>
                    <div className="buttons-container">
                        <ModalButtons artData={artData} onClose={onClose} />
                    </div>
                    <div className="p-copy">
                        <Price artData={artData} />
                        <Claim artData={artData} />
                    </div>
                    {artData && (
                        <div>
                            <div className="header-container center">
                                <div key={artData.art.id} className="artwork-box">
                                    <div className="header-container bottom">
                                        <Box
                                            caption={'NFT'}
                                            text={'#' + (artData.tokenId ? artData.tokenId : artData.art.id)}
                                        />
                                        <Box caption={'Title'} text={artData.art.title} />
                                    </div>
                                    <div className="header-container bottom">
                                        <Box caption={'Description'} text={artData.art.prompt} />
                                    </div>
                                    <LoadImageButtonOnDemand artData={artData} />
                                    <div className={'header-container'}>
                                        <div id={'votes' + artData.art.id} className="p-copy">
                                            <Box caption={'Claim Power'} text={artData.claimPower.toFixed(2)} />
                                        </div>
                                        <div id={'claims-made' + artData.art.id} className="p-copy">
                                            <Box
                                                caption={'Claims Made'}
                                                id={'claims-made' + artData.art.id}
                                                text={artData.totalClaims.toFixed(2)}
                                            />
                                        </div>
                                        <div id={'claims-received' + artData.art.id} className="p-copy">
                                            <Box
                                                caption={'Claims Received'}
                                                id={'claims-received' + artData.art.id}
                                                text={artData.totalClaimed.toFixed(2)}
                                            />
                                        </div>
                                    </div>
                                    <div className={'header-container bottom'}>
                                        <Box
                                            caption={'Sale Price'}
                                            text={priceText(artData)}
                                            textValue={artData?.art.price}
                                            triggerAnimation={false}
                                        />
                                        <Box
                                            caption={'Transfer Fee'}
                                            text={transferFeeText(artData)}
                                            textValue={artData?.art.transferFee}
                                            triggerAnimation={false}
                                        />
                                    </div>
                                    {artData.highestBid && (
                                        <div>
                                            <div className={'header-container'}>
                                                <Box
                                                    caption={'Highest Bid'}
                                                    text={crazyAmount(artData.highestBid, `white large-bold`)}
                                                    textValue={artData.highestBid}
                                                    styleCaption={'gradient-text bold large-bold '}
                                                    styleText={'center nav__voted-amount large'}
                                                />
                                            </div>
                                            <Button
                                                btnName={'Accept Bid'}
                                                classStyles={'nav__medium '}
                                                disabled={!accountSignature || !currentAccount || loading}
                                                handleClick={() =>
                                                    accountSignature
                                                        ? acceptBid(artData)
                                                        : alert('Please connect your wallet to proceed transaction')
                                                }
                                                cancelBg={undefined}
                                            />
                                        </div>
                                    )}
                                    {artData.highestOffer && (
                                        <div>
                                            <div className={'header-container'}>
                                                <div className={'nav__balance menu-wrapper-column border margin-box'}>
                                                    <span className={'nav__voted-amount large'}>
                                                        {crazyAmount(artData.highestOffer)}
                                                        <span className={'gradient-text bold large-bold '}>
                                                            Highest Offer
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                            <Button
                                                btnName={'Accept Offer'}
                                                classStyles={'nav__medium '}
                                                disabled={!accountSignature || !currentAccount || loading}
                                                handleClick={() =>
                                                    accountSignature
                                                        ? acceptOffer(artData)
                                                        : alert('Please connect your wallet to proceed transaction')
                                                }
                                                cancelBg={undefined}
                                            />
                                        </div>
                                    )}
                                    <VotesTooltip artData={artData} />
                                    <ClaimsMadeTooltip artData={artData} />
                                    <ClaimsReceivedTooltip artData={artData} />
                                </div>
                            </div>
                            <DetailsTabs artData={artData} tabName={tabName} />
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

ModelMyMarketCardDetails.whyDidYouRender = true

export default ModelMyMarketCardDetails
